import { useEffect, useState } from 'react'
import { ListChecks, ShieldCheck, Ticket } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { adminMutate, apiGet, Select, usePermissions, useToast } from './primitives.js'
import { Field, NumberInput, SwitchRow, TextInput } from './form.js'
import { useSaveBar } from '../shell/editing.js'
import { useIntegrationCatalog, IntegrationStatusPill } from '../navigation/integration-status.js'
import { CleAccesPanel } from './CleAccesPanel.js'
import { pastilleLecturePretix, phraseComptabilitePretix } from './pretix-integration-model.js'

/**
 * ADMINISTRATION → INTÉGRATIONS → PRETIX (16/10/2026).
 *
 * Pretix vend l'entrée des soirées d'ASSOCIATIONS (BDE). Gestion en lit les
 * entrées pour le total de la nuit — « 636 entrées cette nuit : L'ÉTUDIANTE
 * 421 (Shotgun) · MEDECINE 215 (Pretix) ». Ce que la Comptabilité en fait
 * (entrées, chiffre d'affaires) suit ses Règles : la page reprend la ligne
 * du catalogue, jamais une phrase figée.
 *
 * Trois blocs, dans l'ordre où on s'en sert la première fois :
 *   1. créer dans Pretix l'accès en LECTURE (une équipe qui ne voit que les
 *      commandes, et son jeton) — les étapes, en clair ;
 *   2. la clé d'accès : le panneau commun (essai avant enregistrement,
 *      ré-authentification) ;
 *   3. la lecture : activation (impossible sans clé), adresse, organisateur,
 *      événements suivis, cadences — avec la Save Bar.
 */

interface PretixConfig {
  enabled: boolean
  base_url: string
  organizer: string
  events_scope: 'ALL' | 'SOLD_ONLY'
  refresh_night_s: number
  refresh_idle_s: number
  token_configured: boolean
  token_updated_at: string | null
  status: string
  last_success_at: string | null
  last_attempted_at: string | null
  last_error: string | null
  last_read_at: string | null
  bounds: { refresh_night_s: { min: number; max: number }; refresh_idle_s: { min: number; max: number } }
}

interface Saisie {
  enabled: boolean
  base_url: string
  organizer: string
  events_scope: 'ALL' | 'SOLD_ONLY'
  refresh_night_s: string
  /** En MINUTES à l'écran ; en secondes côté serveur. */
  refresh_idle_min: string
}

const fmtDT = (iso: string | null) => iso === null ? 'jamais' : new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})

const versSaisie = (c: PretixConfig): Saisie => ({
  enabled: c.enabled, base_url: c.base_url, organizer: c.organizer, events_scope: c.events_scope,
  refresh_night_s: String(c.refresh_night_s), refresh_idle_min: String(Math.round(c.refresh_idle_s / 60)),
})

const memeSaisie = (a: Saisie, b: Saisie) =>
  a.enabled === b.enabled && a.base_url.trim() === b.base_url && a.organizer.trim() === b.organizer
  && a.events_scope === b.events_scope && Number(a.refresh_night_s) === Number(b.refresh_night_s)
  && Number(a.refresh_idle_min) === Number(b.refresh_idle_min)

/** Pourquoi Enregistrer est grisé — en clair, ou null. */
function invalide(s: Saisie, c: PretixConfig): string | null {
  // https:// seulement : la clé part dans chaque requête, jamais en clair.
  if (!/^https:\/\/\S+$/i.test(s.base_url.trim())) return 'Adresse de Pretix refusée : elle doit commencer par https:// (en http://, la clé partirait en clair).'
  if (!/^[A-Za-z0-9._-]{1,50}$/.test(s.organizer.trim())) return 'Identifiant d’organisateur illisible (lettres, chiffres, point ou tiret).'
  const nuit = Number(s.refresh_night_s)
  const repos = Number(s.refresh_idle_min) * 60
  if (!Number.isInteger(nuit) || nuit < c.bounds.refresh_night_s.min || nuit > c.bounds.refresh_night_s.max) {
    return `Pendant une nuit : de ${c.bounds.refresh_night_s.min} à ${c.bounds.refresh_night_s.max} secondes.`
  }
  if (!Number.isInteger(repos) || repos < c.bounds.refresh_idle_s.min || repos > c.bounds.refresh_idle_s.max) {
    return `Hors soirée : de ${c.bounds.refresh_idle_s.min / 60} à ${c.bounds.refresh_idle_s.max / 60} minutes.`
  }
  if (s.enabled && !c.token_configured) return 'Enregistrez d’abord la clé d’accès (panneau ci-dessous).'
  return null
}

/** Les étapes à suivre dans Pretix, avant tout : un accès qui ne fait que LIRE. */
function EtapesPretix() {
  return (
    <section className="lc-panel cle-panel" aria-labelledby="pretix-etapes">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="pretix-etapes"><ListChecks size={16} aria-hidden="true" /> Créer l’accès en lecture dans Pretix</h2>
      </header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">
          Gestion a besoin d’un jeton qui ne peut QUE lire les commandes. Il se crée une fois, par un administrateur de Pretix.
        </p>
        {/* Les libellés exacts de Pretix 2026.3 en français : aucune case
            « voir seulement les commandes » n'existe ; la case « Toutes les
            autorisations relatives aux événements » donnerait l'écriture. */}
        <ol className="cle-etapes">
          <li>Ouvrez Pretix (l’adresse est rappelée dans « Clé d’accès ») et connectez-vous en administrateur.</li>
          <li>Menu de l’organisateur → <strong>Équipes</strong> → <strong>Créer une nouvelle équipe</strong>, nommée « Gestion (lecture) ».</li>
          <li>Laissez <strong>décochées</strong> « Toutes les autorisations d’organisateur » et « Toutes les autorisations relatives aux événements » : elles donnent le droit de modifier et de rembourser. Cochez <strong>« Tous les événements (y compris ceux nouvellement créés) »</strong>.</li>
          <li>Dans « Autorisations de l’événement », ligne <strong>« Commandes » : « Tout afficher »</strong>. Toutes les autres lignes sur leur choix le plus faible (« Voir », « Accès impossible » ou « Interdit ») — jamais « Afficher et modifier ». Enregistrez.</li>
          <li>Sur la page de l’équipe, section <strong>« Tokens API »</strong> : « Nom du token » = le nom proposé dans « Clé d’accès », puis <strong>« Ajouter »</strong>. Copiez le token : Pretix ne le montre qu’une fois.</li>
          <li>Collez-le dans « Clé d’accès » ci-dessous, <strong>testez-le</strong> (le test refuse un token qui ne voit aucune commande), enregistrez-le. Allumez ensuite la lecture.</li>
        </ol>
        <p className="lc-hint">
          <ShieldCheck size={14} aria-hidden="true" /> Mode sûr : lecture seule, sans exception. Gestion n’écrit rien dans Pretix,
          et ne lit ni les noms, ni les e-mails, ni les téléphones des acheteurs.
        </p>
      </div>
    </section>
  )
}

/** Les champs que seul le serveur écrit (état de lecture, clé) — jamais la saisie. */
const etatServeur = (c: PretixConfig): Partial<PretixConfig> => ({
  status: c.status, last_success_at: c.last_success_at, last_attempted_at: c.last_attempted_at,
  last_error: c.last_error, last_read_at: c.last_read_at, token_configured: c.token_configured,
  token_updated_at: c.token_updated_at,
})

/** Après un enregistrement, l'état est relu toutes les 4 s pendant une minute : la première lecture arrive en quelques secondes. */
const SUIVI_LECTURE_MS = 60_000
const PAS_SUIVI_MS = 4_000

function LecturePretix({ onChange, relire, compta }: { onChange: () => void; relire: number; compta: string }) {
  const toast = useToast()
  const permissions = usePermissions()
  const peutConfigurer = permissions.permissions.includes('ticketing.configure')
  const [cfg, setCfg] = useState<PretixConfig | null>(null)
  const [saisie, setSaisie] = useState<Saisie | null>(null)
  const [lecture, setLecture] = useState(false)

  const charger = () => {
    apiGet<PretixConfig>('/api/v1/ticketing/pretix/config')
      .then((c) => { setCfg(c); setSaisie(versSaisie(c)) })
      .catch(() => setCfg(null))
  }
  useEffect(charger, [])

  /* Relire l'ÉTAT seulement (clé posée, lecture faite) sans toucher à la
     saisie : une clé enregistrée dans le panneau voisin ouvre l'interrupteur,
     sans effacer une cadence ou un organisateur en cours de modification. */
  const relireEtat = () => {
    apiGet<PretixConfig>('/api/v1/ticketing/pretix/config')
      .then((c) => { if (c && typeof c.status === 'string') setCfg((avant) => (avant ? { ...avant, ...etatServeur(c) } : c)) })
      .catch(() => undefined)
  }
  useEffect(() => { if (relire > 0) relireEtat() }, [relire])
  const [suiviDepuis, setSuiviDepuis] = useState<number | null>(null)
  useEffect(() => {
    if (suiviDepuis === null) return
    const t = window.setInterval(() => {
      if (Date.now() - suiviDepuis > SUIVI_LECTURE_MS) { setSuiviDepuis(null); return }
      relireEtat()
    }, PAS_SUIVI_MS)
    return () => window.clearInterval(t)
  }, [suiviDepuis])
  // La première lecture arrivée (réussie ou non), le suivi s'arrête.
  useEffect(() => {
    if (suiviDepuis !== null && cfg?.last_attempted_at && Date.parse(cfg.last_attempted_at) >= suiviDepuis) setSuiviDepuis(null)
  }, [cfg?.last_attempted_at, suiviDepuis])

  const dirty = cfg !== null && saisie !== null && peutConfigurer && !memeSaisie(saisie, versSaisie(cfg))

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer la lecture Pretix',
    invalid: cfg && saisie ? invalide(saisie, cfg) : null,
    onSave: async () => {
      if (!saisie) return 'Rien à enregistrer.'
      const r = await adminMutate<PretixConfig & { error?: string; message?: string }>('PUT', '/api/v1/ticketing/pretix/config', {
        enabled: saisie.enabled, base_url: saisie.base_url.trim(), organizer: saisie.organizer.trim(),
        events_scope: saisie.events_scope, refresh_night_s: Number(saisie.refresh_night_s),
        refresh_idle_s: Number(saisie.refresh_idle_min) * 60,
      })
      if (r.status !== 200) return r.data.message ?? 'Enregistrement impossible — vérifiez les valeurs.'
      setCfg(r.data); setSaisie(versSaisie(r.data))
      // L'adresse et l'organisateur conditionnent l'essai de la clé : le panneau relit.
      onChange()
      // Le worker lit dans les secondes qui suivent : la pastille suit.
      if (r.data.enabled) setSuiviDepuis(Date.now())
      return true
    },
    onCancel: () => { if (cfg) setSaisie(versSaisie(cfg)) },
    successMessage: 'Lecture Pretix enregistrée.',
  })

  const lireMaintenant = async () => {
    setLecture(true)
    try {
      const r = await adminMutate<{ ok: boolean; detail: string; error?: string }>('POST', '/api/v1/ticketing/pretix/test')
      if (r.status === 200) toast(r.data.ok ? 'ok' : 'error', r.data.ok ? `Lecture réussie avec la clé enregistrée — ${r.data.detail}` : `La lecture a échoué — ${r.data.detail}`)
      else toast('error', 'Lecture impossible (droits insuffisants ?)')
    } finally { setLecture(false) }
  }

  if (cfg === null || saisie === null) return null
  const maj = (p: Partial<Saisie>) => setSaisie({ ...saisie, ...p })

  return (
    <section className="lc-panel cle-panel" aria-labelledby="pretix-lecture">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="pretix-lecture"><Ticket size={16} aria-hidden="true" /> Lecture des entrées</h2>
        {(() => {
          const p = pastilleLecturePretix(cfg)
          return (
            <span className={`lc-pill lc-pill--${p.ton}`}>
              {p.vivant ? <span className="lc-dot lc-dot--ok lc-dot--live" /> : null}{p.libelle}
            </span>
          )
        })()}
      </header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">
          Allumée, elle ajoute au direct les entrées des soirées d’associations — chaque événement Pretix devient une soirée,
          et la nuit les additionne.
        </p>
        <p className="lc-hint">{compta}</p>
        <SwitchRow
          checked={saisie.enabled}
          onChange={(v) => maj({ enabled: v })}
          title="Lecture activée"
          desc={cfg.token_configured
            ? 'Gestion lit les événements, les ventes et les entrées, en ne demandant que ce qui a changé.'
            : 'Impossible tant qu’aucune clé n’est enregistrée.'}
          disabled={!peutConfigurer || (!cfg.token_configured && !saisie.enabled)}
        />
        <Field label="Adresse de Pretix" helper="Celle que l’on ouvre dans le navigateur, sans « /control ».">
          <TextInput value={saisie.base_url} onChange={(v) => maj({ base_url: v })} placeholder="https://tickets.liveclubrennes.com" maxLength={300} ariaLabel="Adresse de Pretix" disabled={!peutConfigurer} />
        </Field>
        <Field label="Organisateur" helper="L’identifiant court de l’organisateur dans Pretix (il apparaît dans les adresses).">
          <TextInput value={saisie.organizer} onChange={(v) => maj({ organizer: v })} placeholder="LCRennes" maxLength={50} ariaLabel="Organisateur Pretix" disabled={!peutConfigurer} />
        </Field>
        <Field label="Événements suivis" helper="Un événement de test resté sans vente ne devient pas une soirée. Le premier billet vendu le fait suivre.">
          <Select
            value={saisie.events_scope}
            onChange={(v) => maj({ events_scope: v === 'ALL' ? 'ALL' : 'SOLD_ONLY' })}
            ariaLabel="Événements suivis"
            options={[
              { value: 'SOLD_ONLY', label: 'Ceux qui ont au moins un billet vendu (conseillé)' },
              { value: 'ALL', label: 'Tous les événements de l’organisateur' },
            ]}
          />
        </Field>
        <div className="cle-reglages__champs">
          <Field label="Pendant une nuit" helper="De l’approche de la première soirée jusqu’à la fin de la nuit.">
            <NumberInput value={saisie.refresh_night_s} onChange={(v) => maj({ refresh_night_s: v })} unit="s"
              min={cfg.bounds.refresh_night_s.min} max={cfg.bounds.refresh_night_s.max} step={5} ariaLabel="Cadence de lecture pendant une nuit, en secondes" />
          </Field>
          <Field label="Hors soirée" helper="Les ventes en ligne n’ont pas besoin d’être suivies à la seconde.">
            <NumberInput value={saisie.refresh_idle_min} onChange={(v) => maj({ refresh_idle_min: v })} unit="min"
              min={cfg.bounds.refresh_idle_s.min / 60} max={cfg.bounds.refresh_idle_s.max / 60} ariaLabel="Cadence de lecture hors soirée, en minutes" />
          </Field>
        </div>
        <dl className="cle-faits">
          <div><dt>Dernière lecture de Pretix</dt><dd>{fmtDT(cfg.last_read_at)}</dd></div>
          <div><dt>Dernière tentative</dt><dd>{fmtDT(cfg.last_attempted_at)}</dd></div>
          {cfg.last_error ? <div><dt>Dernière erreur</dt><dd>{cfg.last_error}</dd></div> : null}
        </dl>
        {peutConfigurer ? (
          <div className="cle-actions">
            <button type="button" className="lc-btn" onClick={() => { void lireMaintenant() }} disabled={lecture || !cfg.token_configured}>
              {lecture ? 'Lecture en cours…' : 'Lire avec la clé enregistrée'}
            </button>
            <Link className="lc-btn lc-btn--ghost" href="/settings/soirees/billetteries">Affichage dans les Soirées</Link>
          </div>
        ) : null}
      </div>
    </section>
  )
}

export function PretixIntegrationPage() {
  const meta = useShellMeta()
  const catalogue = useIntegrationCatalog()
  const canon = catalogue?.find((i) => i.id === 'pretix') ?? null
  // Deux compteurs de relecture, un par sens — des RELECTURES, jamais des
  // remontages : le panneau de clé relit son état quand l'adresse ou
  // l'organisateur changent (ils conditionnent l'essai) ; la lecture relit
  // son état quand une clé est posée (l'interrupteur s'ouvre). Remonter
  // l'un effaçait ce que l'on était en train d'y saisir (une clé testée mais
  // pas enregistrée, une cadence modifiée), sans prévenir.
  const [versionCle, setVersionCle] = useState(0)
  const [versionLecture, setVersionLecture] = useState(0)
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page cle-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title cle-titre"><Ticket size={22} aria-hidden="true" /> Pretix</h1>
            <p className="lc-subtitle cle-sous">
              {canon ? <IntegrationStatusPill status={canon.status} label={canon.status_label} /> : null}
              <span>La billetterie des soirées d’associations — entrées ajoutées au total de la nuit, en lecture.</span>
            </p>
            {canon?.status_detail ? <p className="lc-hint">{canon.status_detail}</p> : null}
          </div>
        </header>
        <EtapesPretix />
        <CleAccesPanel relire={versionCle} integration="pretix" onEnregistree={() => setVersionLecture((v) => v + 1)} />
        <LecturePretix relire={versionLecture} onChange={() => setVersionCle((v) => v + 1)} compta={phraseComptabilitePretix(canon?.capabilities ?? null)} />
      </main>
    </Shell>
  )
}
